import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
// @ts-ignore
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import type { GroupExerciseSeries } from '../../services/exerciseGroupService';

interface GroupTrendChartsProps {
  series: GroupExerciseSeries[];
}

// Validated dark-mode categorical slots (dataviz skill), assigned in fixed order
// per exercise so a series keeps its color regardless of the others present.
const SERIES_COLORS = ['#3987e5', '#199e70', '#c98500', '#008300', '#9085e9', '#e66767', '#d55181', '#d95926'];

interface MergedRow {
  date: string;
  [seriesKey: string]: number | string | null;
}

/**
 * Multi-series trend charts (one line per exercise) for a group. Recharts wants
 * one row per x-value with a column per series, so merge the per-exercise point
 * lists onto a shared, sorted date axis keyed by exercise id.
 */
const GroupTrendCharts: React.FC<GroupTrendChartsProps> = ({ series }) => {
  const withData = useMemo(() => series.filter((s) => s.points.length > 0), [series]);

  const { maxWeightData, e1rmData } = useMemo(() => {
    const dates = new Set<string>();
    for (const s of withData) {
      for (const p of s.points) {
        if (p.date) dates.add(p.date);
      }
    }
    const sortedDates = Array.from(dates).sort();

    const buildRows = (metric: 'maxWeight' | 'estimated1RM'): MergedRow[] =>
      sortedDates.map((date) => {
        const row: MergedRow = { date };
        for (const s of withData) {
          const point = s.points.find((p) => p.date === date);
          row[`ex_${s.exerciseId}`] = point ? point[metric] : null;
        }
        return row;
      });

    return { maxWeightData: buildRows('maxWeight'), e1rmData: buildRows('estimated1RM') };
  }, [withData]);

  if (withData.length === 0) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Няма достатъчно данни за графики в избрания период.
        </Typography>
      </Paper>
    );
  }

  const renderChart = (title: string, data: MergedRow[]) => (
    <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }} className="no-print">
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {withData.map((s, index) => (
            <Line
              key={s.exerciseId}
              type="monotone"
              dataKey={`ex_${s.exerciseId}`}
              name={s.variationName || s.exerciseName}
              stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );

  return (
    <Box>
      {renderChart('Максимално тегло по време (kg)', maxWeightData)}
      {renderChart('Оценен 1ПМ по време (kg)', e1rmData)}
    </Box>
  );
};

export default GroupTrendCharts;
